import { useEditor } from '../store/useEditor'
import { PALETTE_PRESETS } from '../lib/palettes'
import { PRODUCTS, PRODUCT_ORDER } from '../lib/products'
import { sectionOfRun, sectionsOf } from '../lib/sections'
import { bulbsForFeet } from '../lib/materials'

/**
 * THE RUN YOU HAVE SELECTED, IN THE WORKSPACE.
 *
 * The prototype's step 02 inspector: which section the run belongs to, its
 * product and physical spacing, and six named colors with the bulbs drawn on
 * each tile. Selecting a run here selects it in the editor, because it is the
 * same document and the same selection.
 *
 * THE SPACING HERE IS PHYSICAL, and the note says so. Dragging bulbs closer
 * together on the mockup changes how a picture reads; the socket spacing on
 * the wire is what the warehouse counts. Two different numbers that look the
 * same is how a truck arrives short.
 */
export default function RunInspector() {
  const doc = useEditor((s) => s.doc)
  const selectedId = useEditor((s) => s.selectedId)
  const selectLayer = useEditor((s) => s.selectLayer)
  const updateStrand = useEditor((s) => s.updateStrand)

  const run = doc.layers.find((l) => l.id === selectedId)
  const sections = sectionsOf(doc)

  if (!run || run.type !== 'strand') {
    return (
      <>
        <p className="ws-eyebrow">Runs</p>
        <h2>Pick a run</h2>
        {/*
          The card beside this one already says the picture is drawn by the
          real renderer. Two panels a foot apart saying the same sentence is
          one sentence and a wasted panel.
        */}
        <p>Choose a run to set its product, spacing and color.</p>
        <div className="dq-runs">
          {doc.layers.length === 0 ? (
            <p className="muted">Nothing drawn yet. Open the editor and trace the roofline.</p>
          ) : (

            doc.layers.map((l, i) => (
              <button key={l.id} className="section-tag as-btn" onClick={() => selectLayer(l.id)}>
                R{String(i + 1).padStart(2, '0')}
                {l.name?.trim() ? ` · ${l.name}` : ''}
              </button>
            ))
          )}
        </div>
      </>
    )
  }

  const owner = sectionOfRun(sections, run.id)
  const spec = PRODUCTS[run.product] ?? PRODUCTS.C9
  const spacing = spec.spacingInches
  const current =
    PALETTE_PRESETS.find(
      (p) => p.colors.length === run.palette.length && p.colors.every((c, i) => c === run.palette[i]),
    ) ?? null

  return (
    <>
      <p className="ws-eyebrow">Run {run.name}</p>
      <h2>{owner ? owner.label.trim() || 'Unnamed section' : 'Not on a section yet'}</h2>
      <p>
        {owner
          ? `${owner.measurement.feet} ft · ${bulbsForFeet(owner.measurement.feet, spacing)} bulbs at ${spacing} inch spacing`
          : 'Attach this run to a place on the house in step 01 and it starts counting.'}
      </p>

      <label>
        Product
        <select
          value={run.product}
          onChange={(e) => updateStrand(run.id, { product: e.target.value as typeof run.product })}
        >
          {PRODUCT_ORDER.map((id) => (
            <option key={id} value={id}>
              {PRODUCTS[id].label} · {PRODUCTS[id].spacingInches} in
            </option>
          ))}
        </select>
      </label>
      <p className="field-help">
        Spacing is the socket spacing on the wire, which is what the count is built on. The
        mockup's spacing slider changes how it reads, not what is ordered.
      </p>

      <h3>Color</h3>
      <div className="color-options">
        {PALETTE_PRESETS.map((p) => (
          <button
            key={p.id}
            aria-pressed={current?.id === p.id}
            onClick={() => updateStrand(run.id, { palette: [...p.colors], patternRepeat: p.patternRepeat })}
          >
            <span className="mini-bulbs">
              {Array.from({ length: 5 }, (_, i) => (
                <i key={i} style={{ '--bulb': p.colors[i % p.colors.length] } as React.CSSProperties} />
              ))}
            </span>
            {p.label}
            {current?.id === p.id && <b aria-hidden="true">&#10003;</b>}
          </button>
        ))}
      </div>
      <p className="field-help">
        All palettes are a repeating sequence, not a gradient. Cost follows the chosen product.
      </p>
    </>
  )
}
